import { SearchInput } from '@lmig/lmds-react-alphanumeric-input';
import { GridRow, GridCol } from '@lmig/lmds-react-grid';
import { Link } from '@lmig/lmds-react-link';
import { Button } from '@lmig/lmds-react-button';
import { useState, FunctionComponent } from 'react';
import {
  getBLBillingInquiryLink,
  getBLClaimsInquiryLink,
  getBLPolicyInquiryLink,
} from 'Foundation/Configurations/GlobalConfiguration';
import './CustomerAccountLookup.scss';
import { useUserData } from 'Foundation/User/UserContext';
import { Heading } from '@lmig/lmds-react-typography';
import { IconButton, IconInfo } from '@lmig/lmds-react-icons';
import { Popover } from '@lmig/lmds-react-popover';
import { performSearch } from './Helpers/SearchHelper';
import RecentCustomerSearches from './RecentCustomerSearches';

interface CustomerAccountLookupProps {
  // Optional override of the current location.search (useful for tests)
  searchOverride?: string;
}

// eCLIQ policy pattern examples: AB12345678, ABC12345678, AB0012345678, ABC0012345678, ABC(00)12345678, AB(00)12345678
// If a user enters a non-standard policy number, they will need to search by name.
// Customer name searches require at least 2 characters.
const POLICY_INPUT_REGEX = /^(?:([A-Z]{2,3}))?(?:\(?\d{2}\)?)?(\d{8})$/i;

const CustomerAccountLookup: FunctionComponent<CustomerAccountLookupProps> = ({ searchOverride }) => {
  const { userData } = useUserData();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const searchTooltip =
    'For optimal results when searching by policy number, please enter it exactly as it appears on the policy. Most policy numbers have a two or three character prefix followed by numbers, but some vary by insurance system. If you are unable to locate by policy number, try searching by insured name instead.';

  const handleSearch = () => {
    setError(null);
    const result = performSearch(searchTerm, { isAISGUser: Boolean(userData?.isAISGUser), isMockingAgent: Boolean(userData?.isMockingAgent) });
    if (result) {
      setError(result);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <>
      {userData && (
        <div className="customer-account-lookup">
          <Heading type="h4">Customer lookup</Heading>
          <form id="customer-account-lookup-form-combined">
            <GridRow justify="center" className="lookup-text">
              <GridCol>
                <span>Look up a customer by name or policy number.</span>
                <Popover
                  trigger={
                    <IconButton background="transparent">
                      <IconInfo color="teal" size="16" />
                    </IconButton>
                  }
                  direction="below"
                  align="start"
                  className="sc-customer-search-popover"
                  variant="inlineIcon"
                >
                  {searchTooltip}
                </Popover>
              </GridCol>
            </GridRow>
            <GridRow justify="center">
              <GridCol>
                <div data-theme="evolved">
                  <SearchInput
                    labelVisual="Search customer"
                    value={searchTerm}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setError(null);
                      setSearchTerm(e.target.value);
                    }}
                    onClearClicked={() => setSearchTerm('')}
                    onKeyDown={onKeyDown}
                    {...(error ? { status: 'error' } : {})}
                    autoFocus
                  />
                </div>
              </GridCol>
            </GridRow>
            <GridRow justify="center">
              <GridCol>
                <div data-theme="evolved">
                  <Button
                    className="customer-account-lookup-buttons"
                    dynamicWidth
                    variant="primary"
                    onClick={handleSearch}
                  >
                    Search
                  </Button>
                </div>
              </GridCol>
            </GridRow>
            <GridRow>
              <GridCol>
                <RecentCustomerSearches />
              </GridCol>
            </GridRow>
            <GridRow justify="center">
              <GridCol>
                <div className="customer-account-lookup-lower-helper-text">
                  <p>
                    Not seeing the results you expect? Search our{' '}
                    <Link variant="inline" target="_blank" href={getBLPolicyInquiryLink(userData.isAgent())}>
                      Policy
                    </Link>
                    <span>, </span>
                    <Link
                      variant="inline"
                      target="_blank"
                      href={getBLClaimsInquiryLink(
                        userData.isAgent(),
                        userData.dpecid,
                        userData.selectedStatCodeData.SubStat,
                      )}
                    >
                      Claims
                    </Link>
                    <span>, or </span>
                    <Link
                      variant="inline"
                      target="_blank"
                      href={getBLBillingInquiryLink(
                        userData.isAgent(),
                        userData.dpecid,
                        userData.selectedStatCodeData.SubStat,
                      )}
                    >
                      Billing
                    </Link>
                    <span> tools.</span>
                  </p>
                </div>
              </GridCol>
            </GridRow>
          </form>
        </div>
      )}
    </>
  );
};

export default CustomerAccountLookup;
